/**
 * ClosedCompanyCard — how a SEALED company appears anywhere in the app
 * (Phase 119-07, owner decision #2).
 *
 * A closed / acquired / bankrupt company keeps its place on the platform with
 * its status, latest score and score history, and nothing else: its profile and
 * data are sealed. Investors who had it in dealflow, a thread or a submission
 * see this record instead of an empty "Unknown" card.
 *
 * The look is an archive record rather than a live listing: a muted surface, a
 * thin status-coloured spine, the status written out, and the score history as
 * the one vivid element. Presentational only — the data comes from
 * `useCompanyPublicCards` (src/lib/company/public-card.ts), which the caller
 * runs once for a whole list.
 *
 * Branches: loading, error (with Retry), empty (the card could not be found —
 * shown with the caller's own name for the record) and the card.
 */

import React from 'react';
import { View, Text, Pressable, ActivityIndicator } from 'react-native';
import { Archive, Handshake, CircleSlash, RotateCcw, ChevronRight, type LucideIcon } from 'lucide-react-native';
import { CompanyAvatar } from '@/components/CompanyAvatar';
import { ScoreSparkline } from '@/components/score/atoms/ScoreSparkline';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import {
  LIFECYCLE_LABEL,
  formatQuarter,
  historyScores,
  lifecycleLine,
  type CompanyPublicCard,
  type LifecycleStatus,
} from '@/lib/company/public-card';
import { APP_LOCALE } from '@/lib/format';

type Colors = ReturnType<typeof useThemeColors>;

const STATUS_ICON: Record<LifecycleStatus, LucideIcon> = {
  closed: Archive,
  acquired: Handshake,
  bankrupt: CircleSlash,
};

function statusTone(status: LifecycleStatus, colors: Colors): { accent: string; wash: string } {
  switch (status) {
    case 'acquired':
      return { accent: colors.primary[600], wash: colors.primary[50] };
    case 'bankrupt':
      return { accent: colors.red.main, wash: colors.red.light };
    default:
      return { accent: colors.text.tertiary, wash: colors.surface.tertiary };
  }
}

/** One sentence per status, for the detail variant. */
export function lifecycleExplanation(status: LifecycleStatus): string {
  switch (status) {
    case 'acquired':
      return 'This company was acquired. Its profile and data are sealed; its score history stays on record.';
    case 'bankrupt':
      return 'This company went bankrupt. Its profile and data are sealed; its score history stays on record.';
    default:
      return 'This company has closed. Its profile and data are sealed; its score history stays on record.';
  }
}

/** "4 scores · Mar 2025 – Sep 2026" */
export function historySpan(card: Pick<CompanyPublicCard, 'history'>): string | null {
  const n = card.history.length;
  if (n === 0) return null;
  const fmt = (m: string) => {
    const [y, mo] = m.split('-').map(Number);
    const d = new Date(Date.UTC(y, (mo ?? 1) - 1, 1));
    return Number.isFinite(d.getTime())
      ? d.toLocaleDateString(APP_LOCALE, { month: 'short', year: 'numeric', timeZone: 'UTC' })
      : m;
  };
  const first = card.history[0].month;
  const last = card.history[n - 1].month;
  const range = first === last ? fmt(first) : `${fmt(first)} – ${fmt(last)}`;
  return `${n} score${n === 1 ? '' : 's'} · ${range}`;
}

export interface ClosedCompanyCardProps {
  card: CompanyPublicCard | null | undefined;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  /** The caller's own name for the record (e.g. the dealflow row), used when the card has none. */
  fallbackName?: string | null;
  variant?: 'compact' | 'detail';
  onPress?: () => void;
  testID?: string;
}

export function ClosedCompanyCard({
  card,
  loading = false,
  error,
  onRetry,
  fallbackName,
  variant = 'compact',
  onPress,
  testID = 'closed-company-card',
}: ClosedCompanyCardProps) {
  const colors = useThemeColors();
  const pad = isWeb ? 20 : 16;
  const avatar = variant === 'detail' ? (isWeb ? 56 : 48) : isWeb ? 44 : 40;

  const shell = (children: React.ReactNode, accent: string, state: string) => (
    <View
      testID={testID}
      accessibilityLabel={state}
      style={{
        flexDirection: 'row',
        borderRadius: 18,
        overflow: 'hidden',
        backgroundColor: colors.surface.primary,
        borderWidth: 1,
        borderColor: colors.border.light,
      }}
    >
      <View style={{ width: 4, backgroundColor: accent }} />
      <View style={{ flex: 1, padding: pad, gap: 12 }}>{children}</View>
    </View>
  );

  if (loading) {
    return shell(
      <View className="flex-row items-center" style={{ gap: 12, minHeight: avatar }}>
        <ActivityIndicator color={colors.text.tertiary} />
        <Text numberOfLines={1} className="flex-1" style={{ color: colors.text.secondary, fontSize: 14 }}>
          {fallbackName ? `Loading ${fallbackName}…` : 'Loading company…'}
        </Text>
      </View>,
      colors.border.medium,
      'Loading company',
    );
  }

  if (error) {
    return shell(
      <>
        <View className="flex-row items-center" style={{ gap: 12 }}>
          <View className="flex-1">
            <Text numberOfLines={1} style={{ color: colors.text.primary, fontSize: 15, fontWeight: '600' }}>
              {fallbackName ?? 'Company'}
            </Text>
            <Text numberOfLines={2} style={{ color: colors.text.secondary, fontSize: 13, marginTop: 2 }}>
              Couldn&apos;t load this company&apos;s record.
            </Text>
          </View>
          {onRetry && (
            <View
              className="shrink-0"
              style={{ height: 44, borderRadius: 12, backgroundColor: colors.surface.tertiary, overflow: 'hidden' }}
            >
              <Pressable
                testID={`${testID}-retry`}
                accessibilityRole="button"
                accessibilityLabel="Retry"
                onPress={onRetry}
                style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingHorizontal: 14 }}
              >
                <RotateCcw size={16} color={colors.text.primary} />
                <Text numberOfLines={1} style={{ color: colors.text.primary, fontSize: 14, fontWeight: '600' }}>
                  Retry
                </Text>
              </Pressable>
            </View>
          )}
        </View>
      </>,
      colors.amber.main,
      'Company record unavailable',
    );
  }

  if (!card) {
    return shell(
      <View>
        <Text numberOfLines={1} style={{ color: colors.text.primary, fontSize: 15, fontWeight: '600' }}>
          {fallbackName ?? 'Company'}
        </Text>
        <Text numberOfLines={2} style={{ color: colors.text.secondary, fontSize: 13, marginTop: 2 }}>
          This company is no longer listed on Inverio.
        </Text>
      </View>,
      colors.border.medium,
      'Company no longer listed',
    );
  }

  const tone = statusTone(card.status, colors);
  const Icon = STATUS_ICON[card.status];
  const name = card.name ?? fallbackName ?? 'Company';
  const scores = historyScores(card);
  const latest = card.latest?.finalScore ?? null;
  const span = historySpan(card);
  const quarter = formatQuarter(card.declaredQuarter);

  const body = (
    <>
      <View className="flex-row items-center" style={{ gap: 12 }}>
        <View className="shrink-0" style={{ opacity: 0.85 }}>
          <CompanyAvatar name={name} logoUrl={card.logoUrl} size={avatar} radius={12} />
        </View>
        <View className="flex-1">
          <Text
            numberOfLines={1}
            style={{ color: colors.text.primary, fontSize: variant === 'detail' ? 20 : 16, fontWeight: '700', letterSpacing: -0.2 }}
          >
            {name}
          </Text>
          <View className="flex-row items-center" style={{ gap: 6, marginTop: 4 }}>
            <View
              className="shrink-0 flex-row items-center"
              style={{ gap: 4, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 999, backgroundColor: tone.wash }}
            >
              <Icon size={12} color={tone.accent} />
              <Text numberOfLines={1} style={{ color: tone.accent, fontSize: 11, fontWeight: '700', letterSpacing: 0.6 }}>
                {LIFECYCLE_LABEL[card.status].toUpperCase()}
              </Text>
            </View>
            {quarter && (
              <Text numberOfLines={1} className="flex-1" style={{ color: colors.text.tertiary, fontSize: 12 }}>
                {quarter}
              </Text>
            )}
          </View>
        </View>
        <View className="shrink-0 items-end">
          <Text
            testID={`${testID}-score`}
            numberOfLines={1}
            style={{ color: latest === null ? colors.text.tertiary : colors.text.primary, fontSize: variant === 'detail' ? 30 : 24, fontWeight: '800', fontVariant: ['tabular-nums'] }}
          >
            {latest === null ? '—' : Math.round(latest)}
          </Text>
          <Text numberOfLines={1} style={{ color: colors.text.tertiary, fontSize: 10, fontWeight: '600', letterSpacing: 0.6 }}>
            LAST SCORE
          </Text>
        </View>
        {onPress && variant === 'compact' && (
          <View className="shrink-0">
            <ChevronRight size={18} color={colors.text.tertiary} />
          </View>
        )}
      </View>

      <View
        className="flex-row items-end"
        style={{ gap: 12, paddingTop: 10, borderTopWidth: 1, borderTopColor: colors.border.light }}
      >
        <View className="flex-1">
          <Text numberOfLines={1} style={{ color: colors.text.secondary, fontSize: 12 }}>
            {span ?? 'No score on record'}
          </Text>
        </View>
        {scores.length >= 2 && (
          <View className="shrink-0" testID={`${testID}-sparkline`}>
            {/* The history is the card's one vivid element: a stronger tint and
                denser earlier bars than the default sparkline (119-07 UAT: the
                grey "closed" bars at 25% alpha were barely visible). */}
            <ScoreSparkline
              data={scores.slice(-24)}
              color={card.status === 'closed' ? colors.text.secondary : tone.accent}
              restAlpha="99"
              height={variant === 'detail' ? 36 : 28}
              barWidth={5}
            />
          </View>
        )}
      </View>

      {variant === 'detail' && (
        <Text numberOfLines={3} style={{ color: colors.text.secondary, fontSize: 14, lineHeight: 20 }}>
          {lifecycleExplanation(card.status)}
        </Text>
      )}
    </>
  );

  const label = `${name}, ${lifecycleLine(card)}${latest === null ? '' : `, last score ${Math.round(latest)}`}`;

  if (onPress) {
    return (
      <Pressable testID={`${testID}-press`} accessibilityRole="button" accessibilityLabel={label} onPress={onPress}>
        {shell(body, tone.accent, label)}
      </Pressable>
    );
  }
  return shell(body, tone.accent, label);
}

export default ClosedCompanyCard;
